import { Button } from '@pancakeswap/uikit'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { openPwaSheet } from './events'
import { promptInstall, useInstallPrompt } from './installPrompt'
import { PwaEnv, detectEnv } from './platform'
import { VisitKind, markShownThisSession, readHint, shownThisSession, visitKind, writeHint } from './storage'

/** Pages whose first screen is a form the visitor came for: the line never pushes it down (site review 59 #2). */
export const NO_HINT_ROUTES = new Set(['/swap'])

/**
 * One-time install hint on phones (Bez, 2026-10-07). It sits IN the page flow at the top of the content, so it never covers a
 * control or the bottom bar, and it is gone for good once dismissed (or after the visit it was first shown in).
 * Not on a first visit and never on /swap (site review 59 #2, 2026-10-07): on a phone's first screen it pushed the swap button under
 * the bottom bar. It first appears on a later visit, on any other page.
 * Android/Chrome: appears when the browser offers its install prompt; if that arrives after the visitor already started tapping,
 * it waits for the next page so nothing moves under a finger. iOS Safari: opens the Share -> Add to Home Screen steps.
 * Never in the installed app, on desktop, or in a wallet's in-app browser. The More menu keeps "Install app" either way.
 */
const Bar = styled.section`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 12px 0;
  padding: 8px 6px 8px 12px;
  min-height: 56px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  box-sizing: border-box;
  max-width: 640px;
  @media (min-width: 640px) {
    margin: 12px auto 0;
  }
  @media (min-width: 852px) {
    display: none;
  }
`
const Icon = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex: none;
`
const Copy = styled.div`
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`
const Title = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`
const Sub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSubtle};
`
const Close = styled.button`
  flex: none;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSubtle};
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
  }
`

export default function InstallHint() {
  const router = useRouter()
  const { canPrompt, installed } = useInstallPrompt()
  const [env, setEnv] = useState<PwaEnv | null>(null)
  const [eligible, setEligible] = useState(false)
  const [visit, setVisit] = useState<VisitKind | null>(null)
  const [closed, setClosed] = useState(false)
  const [waitForPage, setWaitForPage] = useState(false)
  const interacted = useRef(false)
  const sawPrompt = useRef(false)

  useEffect(() => {
    setEnv(detectEnv(window))
    const state = readHint()
    setVisit(visitKind())
    setEligible(state === null || (state === 'shown' && shownThisSession()))
    const onPointer = () => {
      interacted.current = true
    }
    window.addEventListener('pointerdown', onPointer, { capture: true, passive: true })
    return () => window.removeEventListener('pointerdown', onPointer, { capture: true })
  }, [])

  const mode = !env || env.standalone || !env.mobile || env.inApp || installed ? null : canPrompt ? 'prompt' : env.ios ? 'ios' : null

  useEffect(() => {
    if (mode === 'prompt' && !sawPrompt.current) {
      sawPrompt.current = true
      if (interacted.current) setWaitForPage(true)
    }
  }, [mode])

  useEffect(() => {
    if (!waitForPage) return undefined
    const done = () => setWaitForPage(false)
    router?.events.on('routeChangeComplete', done)
    return () => router?.events.off('routeChangeComplete', done)
  }, [waitForPage, router])

  useEffect(() => {
    if (installed) writeHint('installed')
  }, [installed])

  // a hint already shown in this visit stays for the visit; a new one waits for a later visit
  const visitOk = visit === 'returning' || shownThisSession()
  const onFormPage = NO_HINT_ROUTES.has(router?.pathname ?? '')
  const visible = !!mode && eligible && visitOk && !onFormPage && !closed && !waitForPage

  useEffect(() => {
    if (visible && !shownThisSession()) {
      writeHint('shown')
      markShownThisSession()
    }
  }, [visible])

  if (!visible) return null

  const install = async () => {
    if (mode === 'prompt') {
      const outcome = await promptInstall()
      if (outcome === 'accepted') writeHint('installed')
      return
    }
    openPwaSheet('install')
  }
  const dismiss = () => {
    setClosed(true)
    writeHint('dismissed')
  }

  return (
    <Bar data-testid="pwa-install-hint" aria-label="Install CADINU">
      <Icon src="/icons/pwa/icon-192.png" alt="" width={32} height={32} />
      <Copy>
        <Title>Install CADINU</Title>
        <Sub>Opens full screen, like an app.</Sub>
      </Copy>
      <Button scale="sm" onClick={install} data-testid="pwa-install-hint-action" style={{ flex: 'none', height: 36, padding: '0 14px' }}>
        Install
      </Button>
      <Close type="button" aria-label="Dismiss" title="Dismiss" onClick={dismiss} data-testid="pwa-install-hint-dismiss">
        ×
      </Close>
    </Bar>
  )
}
